import { HttpTypes } from '@/types';
import { IFaq } from '@/types/faq';
import { ITermsCondition } from '@/types/general';
import {
  AdminAddBaseFeeSetting,
  AdminAddMultiOriginSetting,
  AdminFeeSettingResponse,
  AdminMultiOriginSettingResponse,
  BaseFeeSetting,
  MultiOriginSetting,
} from '@/types/http';
import { BaseTaxSetting } from '@/types/http/order-preference/common';
import { AxiosInstance } from 'axios';

class GeneralSetting {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async getTax(): Promise<HttpTypes.IResponse<BaseTaxSetting>> {
    const result = await this.client.get(`/sites/taxes`);
    return result.data;
  }

  async updateTax(data: BaseTaxSetting): Promise<HttpTypes.IResponse<BaseTaxSetting>> {
    const result = await this.client.put(`/sites/taxes`, data);
    return result.data;
  }

  async updateMultiOrigin(
    data: AdminAddMultiOriginSetting
  ): Promise<HttpTypes.IResponse<AdminMultiOriginSettingResponse>> {
    const result = await this.client.post(`/sites/multi-origin`, data);
    return result.data;
  }

  async getMultiOrigin(): Promise<HttpTypes.IResponse<MultiOriginSetting>> {
    const result = await this.client.get(`/sites/multi-origin`);
    return result.data;
  }

  async getChargeFee(): Promise<HttpTypes.IResponse<BaseFeeSetting>> {
    const result = await this.client.get(`/sites/get-store`);
    return result.data;
  }

  async updateChargeFee(
    data: AdminAddBaseFeeSetting
  ): Promise<HttpTypes.IResponse<AdminFeeSettingResponse>> {
    const result = await this.client.put(`/sites/update-store`, data);
    return result.data;
  }

  async getGlobalSetting(): Promise<HttpTypes.IResponse<HttpTypes.AdminGlobalSettingResponse>> {
    const result = await this.client.get(`/sites/global-setting`);
    return result.data;
  }

  async getProductSetting(): Promise<HttpTypes.AdminProductSettingResponse> {
    const result = await this.client.get(`/sites/product-setting`);
    return result.data;
  }

  async updateProductSetting(
    data: HttpTypes.AdminUpdateProductSetting
  ): Promise<HttpTypes.AdminProductSettingResponse> {
    const result = await this.client.post(`/sites/product-setting`, data);
    return result.data;
  }

  async createFaq(data: IFaq[]): Promise<HttpTypes.IResponse<IFaq[]>> {
    const result = await this.client.post(`/sites/faq`, data);
    return result.data;
  }

  async getFaq(): Promise<HttpTypes.IResponse<IFaq[]>> {
    const result = await this.client.get(`/sites/faq`);
    return result.data;
  }

  async getActivityLogs(query: HttpTypes.FindParams): Promise<HttpTypes.AdminActivityLogResponse> {
    const result = await this.client.get(`/sites/activity-logs`, {
      params: query,
    });

    return result.data;
  }

  async createTermsCondition(data: ITermsCondition): Promise<HttpTypes.IResponse<ITermsCondition>> {
    const result = await this.client.put(`/sites/terms-condition`, data);
    return result.data;
  }

  async getTermsCondition(): Promise<HttpTypes.IResponse<ITermsCondition>> {
    const result = await this.client.get(`/sites/terms-condition`);
    return result.data;
  }

  async updateFreshChatId({
    user_id,
    restore_id,
  }: {
    user_id: string;
    restore_id: string;
  }): Promise<HttpTypes.IResponse> {
    const result = await this.client.post(`/user/session/restore`, {
      user_id,
      restore_id,
    });
    return result.data;
  }

  async getUnreadNotificationCount(): Promise<
    HttpTypes.IResponse<{ inbox: number; announcement: number }>
  > {
    const result = await this.client.get(`/sites/notifications/count`);
    return result.data;
  }

  async getNotifications(
    params: HttpTypes.FindParams
  ): Promise<HttpTypes.AdminNotificationsResponse> {
    const result = await this.client.get('/sites/notifications', {
      params,
    });

    return result.data;
  }

  async markNotificationAsRead(
    data: HttpTypes.AdminNotificationMarkAsReadPayload
  ): Promise<HttpTypes.IResponse> {
    const result = await this.client.post(`/sites/notifications/read`, data);
    return result.data;
  }

  async markAllNotificationAsRead(data: { type: string }): Promise<HttpTypes.IResponse> {
    const result = await this.client.post(`/sites/notifications/read-all`, data);
    return result.data;
  }

  async getGeneralOrderSetting(): Promise<HttpTypes.AdminGeneralOrderSettingResponse> {
    const result = await this.client.get(`/sites/order-setting`);
    return result.data;
  }

  async updateGeneralOrderSetting(
    data: HttpTypes.AdminUpdateGeneralOrderSetting
  ): Promise<HttpTypes.IResponse> {
    const result = await this.client.post(`/sites/order-setting`, data);
    return result.data;
  }

  async getPickupInStoreSetting(): Promise<HttpTypes.AdminPickupInStoreSetting> {
    const result = await this.client.get(`/sites/pickup-in-store`);
    return result.data;
  }

  async updatePickupInStoreSetting(
    data: HttpTypes.AdminPickupInStoreSetting
  ): Promise<HttpTypes.IResponse<HttpTypes.AdminPickupInStoreSetting>> {
    const result = await this.client.post(`/sites/pickup-in-store`, data);
    return result.data;
  }

  async updateOrderSetting(data: HttpTypes.AdminOrderSetting): Promise<HttpTypes.IResponse> {
    const result = await this.client.post(`/sites/order-setting`, data);
    return result.data;
  }
}

export default GeneralSetting;
